// DOM overlay: scoreboard with the three points, HP plates over heroes, skill bar, respawn and victory
// banners. Ported from prototypes/match/js/hud.js. Three adaptations for core's data-only events: a
// `num` event's `hero` is an id (resolved here before use), the minimap's RMB-to-move now goes through
// game.apply() like every other order instead of calling game.orderMove() directly, and the vision
// polygons the minimap's fog needs are handed in by main.ts (client presentation state) rather than
// stashed on the core Game object the way the prototype's index.html did it.
import * as THREE from 'three';
import { RULES } from '../core/game.js';
import { SKILLS } from '../core/skills.js';
import { passiveInfo } from './heroInfo.js';
import { ARTIFACTS, vialCharge } from '../core/artifacts.js';
import { inBrush, LAYOUT, baseParts, inEllipse, type Team } from '../core/layout.js';
import type { ArtifactLine, ArtifactSlotKey, CastKey, RelicLine } from '../core/commands.js';
import type { RelicEvent } from '../core/events.js';
import { RELICS } from '../core/relics.js';
import { skillTipHtml, artifactTipHtml, relicTipHtml } from './skillTip.js';
import { heroStats, heroStatsRowsHtml, heroStatusHtml, statInputsOf } from './heroStats.js';
import { MONSTERS, type MonsterKind } from '../core/monsters.js';
import { MONSTER_LOOK } from './monsterView.js';
import { isMonsterUnit, isSummonUnit, type Unit } from './input.js';
import { SUMMON_LOOK } from './summonView.js';
import { buildShopItems, buildRelicItems, heldOf, cdSecondsLeft, vialPct, shopBodyHtml, iconSrc, lineName, relicName, INK_MILESTONES, ARTIFACT_COST, SELL_REFUND, type ShopItemVM, type RelicItemVM, type HeldArtifacts } from './shop.js';
import { LocalGameView, type GameView, type ViewHero, type ViewMonster } from './gameView.js';
import { mountResults } from './results.js';
import { viewerConfused, shownTeam } from './delusion.js';
import { t } from './i18n/index.js';
import { artifactName, className, heroName, monsterName, passiveName, relicName as relicNameOf, skillName, teamFullName, teamName } from './i18n/names.js';

type Input = { targeting: CastKey | null };
export type VisionState = { polys: [number, number][][] };

const ARTIFACT_SLOTS = ['E', 'R'] as const;
const BUFF_KINDS: MonsterKind[] = ['behemoth', 'griffin'];
export function buffText(kind: MonsterKind): string {
  const { hpPct, moveSpeed, atkSpeed } = MONSTERS[kind].buff, pct = (v: number) => `+${Math.round(v * 100)}%`;
  const parts: string[] = [];
  if (hpPct) parts.push(t('buff.hp', { pct: pct(hpPct) }));
  if (moveSpeed && moveSpeed === atkSpeed) parts.push(t('buff.moveAndAttack', { pct: pct(moveSpeed) }));
  else {
    if (moveSpeed) parts.push(t('buff.move', { pct: pct(moveSpeed) }));
    if (atkSpeed) parts.push(t('buff.attack', { pct: pct(atkSpeed) }));
  }
  return parts.join(', ');
}
const mmss = (s: number) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, '0')}`;

/** Why the own hero's Q/W/E/R can't be used right now (Соблазн, Стая's fear), or '' when they can. */
export function silenceReason(forced: 'charm' | 'fear' | null | undefined, left: number): string {
  if (!forced) return '';
  const s = left > 0 ? t('silence.left', { s: Math.ceil(left) }) : '';
  return t(forced === 'charm' ? 'silence.charm' : 'silence.fear', { left: s });
}

/** The announcement both teams get when a relic changes hands. */
export function relicEventText(e: RelicEvent): string {
  return t(e.action === 'bought' ? 'relicFeed.bought' : 'relicFeed.lost', { team: teamName(e.team), relic: relicName(e.line) });
}

/** The relic mark on a holder's plate — the relic's one-word short name, so it can be hunted at a glance. */
export const relicPlateText = (line: RelicLine | null) => (line ? `✦ ${t(`relic.${line}.short` as const)}` : '');

/** The T slot's look: the team's relic, or none. Passive: nothing to press, no cooldown. */
export function relicSlotView(line: RelicLine | null, cd: number): { name: string; empty: boolean; passive: boolean; cooling: boolean; fill: number; cdText: string } {
  if (!line) return { name: t('slot.noRelic'), empty: true, passive: false, cooling: false, fill: 0, cdText: '' };
  const def = RELICS[line];
  if (def.kind === 'passive') return { name: relicNameOf(line), empty: false, passive: true, cooling: false, fill: 0, cdText: '' };
  return { name: relicNameOf(line), empty: false, passive: false, cooling: cd > 0, fill: cd / def.cd, cdText: cd > 0 ? String(Math.ceil(cd)) : '' };
}

/** Short notes under a hero's plate name for the Астрал states that have no CC suffix of their own. */
export function plateTags(st: { invisible?: boolean; confused?: boolean; sleepless?: boolean; silhouette?: boolean }): string {
  return [st.invisible && t('tag.unseen'), st.silhouette && t('status.silhouette'), st.confused && t('status.delusion'), st.sleepless && t('status.sleepless')].filter(Boolean).join(' · ');
}

/** Toasts for what a teammate bought or sold since the last update: ink is one pool per team, so the player
    should see where it went. `seen` is updated in place; a teammate out of sight is forgotten rather than
    diffed against a stale loadout. */
export function allyShopToasts(seen: Map<number, HeldArtifacts>, allies: readonly Pick<ViewHero, 'id' | 'artifacts'>[]): string[] {
  const out: string[] = [], present = new Set<number>();
  for (const a of allies) {
    present.add(a.id);
    const now = heldOf(a.artifacts), was = seen.get(a.id);
    seen.set(a.id, now);
    if (!was) continue;
    for (const k of ARTIFACT_SLOTS) {
      const before = was[k], after = now[k];
      if (before === after) continue;
      if (before) out.push(t('toast.allySold', { name: lineName(before), n: SELL_REFUND }));
      if (after) out.push(t('toast.allyBought', { name: lineName(after), n: ARTIFACT_COST }));
    }
  }
  for (const id of seen.keys()) if (!present.has(id)) seen.delete(id);
  return out;
}

export type SeenMonsters = Map<number, { x: number; z: number; kind: MonsterKind }>;
export const seenMonsters = (monsters: readonly ViewMonster[]): SeenMonsters =>
  new Map(monsters.map(m => [m.id, { x: m.pos.x, z: m.pos.z, kind: m.kind }]));

/* Where a monster's damage number floats: on the monster while it is in view; at its last spot only if it
   was in view on the previous frame (the killing blow — offline it has already left view.monsters by the
   time its own number is read); otherwise nowhere, since the local view's events are unfiltered and a
   fogged monster's numbers would leak who is fighting it and how it's going. */
export function monsterNumAnchor(id: number, monsters: readonly ViewMonster[], prevSeen: SeenMonsters):
  { unit: ViewMonster | null; pos: { x: number; z: number }; kind: MonsterKind } | null {
  const m = monsters.find(v => v.id === id);
  if (m) return { unit: m, pos: m.pos, kind: m.kind };
  const last = prevSeen.get(id);
  return last ? { unit: null, pos: { x: last.x, z: last.z }, kind: last.kind } : null;
}

/* Monster buffs held by each team: own team's row first and drawn solid, the enemy's second and muted. */
function buffRowsHtml(own: Team): string {
  const order: Team[] = own === 'a' ? ['a', 'b'] : ['b', 'a'];
  return order.map(team => `
      <div class="buff-row ${team}${team === own ? ' mine' : ''}" id="buffRow-${team}" hidden>
        <span class="buff-team">${team === own ? t('buff.ours') : t('buff.theirs')}</span>
        ${BUFF_KINDS.map(kind => `<span class="buff" id="buff-${team}-${kind}" title="${monsterName(kind)}: ${buffText(kind)}" hidden><i>${monsterName(kind)[0]}</i><b>${monsterName(kind)}</b><em id="buffT-${team}-${kind}"></em></span>`).join('')}
      </div>`).join('');
}

/** An active E/R slot's look for a given raw `cd`. Склянка крови shows its charge rising instead of a
    cooldown draining: the `vial` look, never `cooling`, the fill growing toward full and the corner number a
    percent. */
export function activeSlotView(line: ArtifactLine, cd: number, held: HeldArtifacts): { cooling: boolean; vial: boolean; fill: number; cdText: string } {
  if (line === 'bloodvial') {
    const charge = vialCharge(cd);
    return { cooling: false, vial: true, fill: charge / ARTIFACTS.bloodvial.heal, cdText: `${vialPct(charge)}%` };
  }
  const maxCd: number = ARTIFACTS[line].kind === 'active' ? (ARTIFACTS[line] as { cd: number }).cd : 0;
  return { cooling: cd > 0, vial: false, fill: maxCd ? cd / maxCd : 0, cdText: cd > 0 ? String(Math.ceil(cdSecondsLeft(cd, held))) : '' };
}

/* Every per-frame HUD write goes through here and reaches the DOM only when the value changed: a same-value
   textContent/style/class write still invalidates style and layout. Assumes nothing else writes the same
   property of the same element behind its back. */
export type DomWriter = ReturnType<typeof createDomWriter>;
export function createDomWriter() {
  type Last = { text?: string; hidden?: boolean; className?: string; cls: Record<string, boolean>; style: Record<string, string>; data: Record<string, string> };
  const last = new Map<HTMLElement, Last>();
  const of = (el: HTMLElement) => {
    let l = last.get(el);
    if (!l) { l = { cls: {}, style: {}, data: {} }; last.set(el, l); }
    return l;
  };
  return {
    text(el: HTMLElement, v: string) { const l = of(el); if (l.text !== v) { l.text = v; el.textContent = v; } },
    hidden(el: HTMLElement, v: boolean) { const l = of(el); if (l.hidden !== v) { l.hidden = v; el.hidden = v; } },
    className(el: HTMLElement, v: string) { const l = of(el); if (l.className !== v) { l.className = v; el.className = v; } },
    toggle(el: HTMLElement, name: string, on: boolean) { const l = of(el); if (l.cls[name] !== on) { l.cls[name] = on; el.classList.toggle(name, on); } },
    style(el: HTMLElement, prop: 'transform' | 'width' | 'height' | 'background' | 'backgroundImage', v: string) { const l = of(el); if (l.style[prop] !== v) { l.style[prop] = v; el.style[prop] = v; } },
    data(el: HTMLElement, key: string, v: string) { const l = of(el); if (l.data[key] !== v) { l.data[key] = v; el.dataset[key] = v; } },
  };
}

/* Minimap redraw gate: a flat list of the numbers the picture depends on (point owners, exact monster and
   hero positions) plus the vision polygons compared vertex for vertex. */
export function createMinimapGate() {
  const sig: number[] = [];
  let n = 0, dirty = true, polys: readonly (readonly [number, number])[][] | null = null;
  return {
    begin() { n = 0; },
    put(v: number) { if (sig[n] !== v) { sig[n] = v; dirty = true; } n++; },
    polys(next: readonly (readonly [number, number])[][]) {
      if (next === polys) return;
      if (!polys || !samePolys(polys, next)) dirty = true;
      polys = next;
    },
    /** True when anything changed since the last call that returned true. */
    end() {
      if (n !== sig.length) { sig.length = n; dirty = true; }
      const d = dirty; dirty = false; return d;
    },
  };
}
function samePolys(a: readonly (readonly [number, number])[][], b: readonly (readonly [number, number])[][]) {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    const pa = a[i]!, pb = b[i]!;
    if (pa.length !== pb.length) return false;
    for (let j = 0; j < pa.length; j++) if (pa[j]![0] !== pb[j]![0] || pa[j]![1] !== pb[j]![1]) return false;
  }
  return true;
}

/* Dota-style damage trail on an HP bar: the main fill snaps to hp immediately; this tracks the separate
   white "ghost" segment that lags behind it. A hit anchors the ghost at the pre-hit hp and restarts the
   hold; with no further hits it eases down to hp after HP_TRAIL_HOLD_MS, over HP_TRAIL_EASE_MS. Healing
   past the ghost clears it. Driven entirely by absolute hp/maxHp, never the hp/maxHp fraction — any maxHp
   change (a buff granted or lost, a respawn) rebases with no ghost, so a maxHp change alone can never fake
   a hit even though it moves the fraction the bar renders. */
export const HP_TRAIL_HOLD_MS = 700;
export const HP_TRAIL_EASE_MS = 300;
const HP_TRAIL_UNSET = -1;

export type HpTrailState = { hp: number; maxHp: number; trailHp: number; anchorHp: number; holdUntil: number };

export function initHpTrail(): HpTrailState {
  return { hp: 0, maxHp: HP_TRAIL_UNSET, trailHp: 0, anchorHp: 0, holdUntil: 0 };
}

/** Rebases an existing state to "no ghost yet" in place — a plate leaving vision or a monster's den going
    empty, without allocating a fresh object every frame it stays that way. */
export function resetHpTrail(s: HpTrailState): void {
  s.hp = 0; s.maxHp = HP_TRAIL_UNSET; s.trailHp = 0; s.anchorHp = 0; s.holdUntil = 0;
}

const easeOutCubic = (t: number) => 1 - (1 - t) ** 3;

/** Mutates `s` in place — called once per visible plate per frame, so it must not allocate. */
export function stepHpTrail(s: HpTrailState, hp: number, maxHp: number, now: number): void {
  if (maxHp !== s.maxHp) { s.hp = hp; s.maxHp = maxHp; s.trailHp = hp; s.anchorHp = hp; s.holdUntil = now; return; }
  if (hp < s.hp) {
    s.anchorHp = Math.max(s.trailHp, s.hp);
    s.trailHp = s.anchorHp;
    s.holdUntil = now + HP_TRAIL_HOLD_MS;
  } else if (hp > s.trailHp) {
    s.trailHp = hp;
    s.anchorHp = hp;
  } else if (now >= s.holdUntil) {
    const t = Math.min(1, (now - s.holdUntil) / HP_TRAIL_EASE_MS);
    s.trailHp = s.anchorHp + (hp - s.anchorHp) * easeOutCubic(t);
  }
  s.hp = hp;
}

/** One HP bar's full per-frame update: hidden plates rebase silently (no catch-up flash on return), shown
    ones step the trail and write both segments' widths — the one path both hero and monster plates share. */
export function updateHpBar(w: DomWriter, bar: { bar: HTMLElement; trail: HTMLElement; hpTrail: HpTrailState },
  shown: boolean, hp: number, maxHp: number, now: number): void {
  if (!shown) { if (bar.hpTrail.maxHp !== HP_TRAIL_UNSET) resetHpTrail(bar.hpTrail); return; }
  stepHpTrail(bar.hpTrail, hp, maxHp, now);
  w.style(bar.bar, 'width', `${Math.max(0, hp / maxHp) * 100}%`);
  w.style(bar.trail, 'width', `${Math.max(0, bar.hpTrail.trailHp / maxHp) * 100}%`);
}

/** Online a buy lands a round trip later, so a second click in that window would see the same slot still
    empty and target it again (the server rejects it). A slot just bought into stays taken until the
    snapshot shows it filled, or BUY_PENDING seconds pass (the buy was rejected). */
const BUY_PENDING = 1;
export function createBuyHold() {
  const boughtAt: Partial<Record<ArtifactSlotKey, number>> = {};
  // A negative age is a hold from before offline's in-place Game.reset() zeroed `t`: expired.
  const pending = (slot: ArtifactSlotKey, t: number) => { const age = t - (boughtAt[slot] ?? -Infinity); return age >= 0 && age < BUY_PENDING; };
  const free = (slot: ArtifactSlotKey, held: HeldArtifacts, t: number) => !held[slot] && !pending(slot, t);
  return {
    pick: (held: HeldArtifacts, t: number): ArtifactSlotKey | null => free('E', held, t) ? 'E' : free('R', held, t) ? 'R' : null,
    bought(slot: ArtifactSlotKey, t: number) { boughtAt[slot] = t; },
    sold(slot: ArtifactSlotKey) { delete boughtAt[slot]; },
  };
}

/** The banner only shows while the results screen can't yet: online, between the final snapshot and
    `matchEnd` (or for good, if the room closed without one). Offline the stats are there at once. */
export function bannerSubText(game: GameView) {
  const next = game instanceof LocalGameView ? '' : t('banner.tallying');
  return `${game.score.a} : ${game.score.b}${next}`;
}

/** The results screen's second button (offline «Ещё матч», online «Смотреть повтор», a replay's «Смотреть снова»). */
export type ResultsSecondary = { label: string; run(): void };

/** `secondary` is asked when the results screen mounts; null = no second button. */
export function createHud(root: HTMLElement, game: GameView, camera: THREE.Camera, stageEl: HTMLElement, vision: VisionState, onPickAgain: () => void, pickAgainLabel = t('common.toMenu'), secondary?: () => ResultsSecondary | null) {
  const L = LAYOUT, player = game.player as ViewHero;
  root.innerHTML = `
    <div class="delusion-veil" id="veil" hidden></div>
    <div class="score" id="score">
      <div class="team a"><span>${teamName('a')}</span><span class="pts" id="ptsA">0</span></div>
      <div class="mid">
        <div class="chips">${game.points.map(p => `<div class="chip" id="chip-${p.id}"><span>${p.id}</span><i></i></div>`).join('')}</div>
        <span class="clock" id="clock">00:00</span>
      </div>
      <div class="team b"><span class="pts" id="ptsB">0</span><span>${teamName('b')}</span></div>
    </div>
    <div class="buffs" id="buffs" hidden>${buffRowsHtml(game.player!.team)}</div>
    <div class="ink" id="ink" title="${t('hud.inkTitle')}"><span class="ink-label">${t('hud.ink')}</span><span class="ink-amt" id="inkAmt">0</span></div>
    <div class="plates" id="plates"></div>
    <div class="stall-hint" id="stallHint" hidden>${t('hud.stallHint')}</div>
    <div class="shop" id="shop" hidden>
      <button type="button" class="shop-close" id="shopClose" aria-label="${t('hud.shopCloseAria')}" title="${t('hud.shopCloseTitle')}">×</button>
      <div class="shop-body" id="shopBody"></div>
    </div>
    <div class="skills" id="skills"></div>
    <div class="hstats" id="hstats">
      <div class="ps-head"><b>${heroName(player.kind)}</b><span>${className(player.def.cls)}</span></div>
      <div class="ps-passive">${passiveName(player.kind)}<em class="ps-tip">${t('common.passiveLine', { name: passiveName(player.kind), desc: passiveInfo(player.kind) })}</em></div>
      <div class="ps-hp" id="hsHp"><span class="bar"><i class="trail"></i><i class="fill"></i></span><span class="ps-hpnum" id="hsHpNum"></span><em class="ps-tip" id="hsHpTip"></em></div>
      <div class="ps-note" id="hsNote" hidden>${t('hud.healBlocked', { name: passiveName('nightmare') })}</div>
      <div id="hsRows"></div>
      <div id="hsStatus"></div>
    </div>
    <div class="status" id="status" hidden></div>
    <div class="banner" id="banner" hidden>
      <span id="bannerTitle"></span>
      <small id="bannerSub"></small>
      <button type="button" class="banner-pick" id="bannerPick">${pickAgainLabel}</button>
    </div>
    <div class="toast" id="toast" hidden></div>
    <div class="relic-feed" id="relicFeed"></div>
    <div class="nums" id="nums"></div>
    <canvas class="minimap" id="minimap" width="240" height="${Math.round(240 * L.bounds.z / L.bounds.x)}" title="${t('hud.minimapTitle')}"></canvas>`;
  const $ = (id: string) => root.querySelector('#' + id) as HTMLElement;
  $('bannerPick').addEventListener('click', onPickAgain);
  const w = createDomWriter();
  const E = {
    ptsA: $('ptsA'), ptsB: $('ptsB'), clock: $('clock'), buffs: $('buffs'), ink: $('ink'), inkAmt: $('inkAmt'), shop: $('shop'),
    shopBody: $('shopBody'), stallHint: $('stallHint'), status: $('status'), banner: $('banner'), bannerTitle: $('bannerTitle'),
    bannerSub: $('bannerSub'), toast: $('toast'), nums: $('nums'), hstats: $('hstats'), relicFeed: $('relicFeed'),
  };
  const hsHpEl = $('hsHp'), hsHpNum = $('hsHpNum'), hsHpTip = $('hsHpTip'), hsRows = $('hsRows'), hsStatus = $('hsStatus'), hsNote = $('hsNote'), veil = $('veil');
  const hsHp = { bar: hsHpEl.querySelector('i.fill') as HTMLElement, trail: hsHpEl.querySelector('i.trail') as HTMLElement, hpTrail: initHpTrail() };
  let hsRowsHtml = '', hsStatusHtml = '';
  function updateHeroStats(nowMs: number) {
    const vm = heroStats(statInputsOf(game));
    updateHpBar(w, hsHp, true, vm.hp, vm.maxHp, nowMs);
    w.text(hsHpNum, `${Math.ceil(vm.hp)} / ${Math.round(vm.maxHp)}`);
    w.className(hsHpEl, `ps-hp${vm.hpTone ? ' ' + vm.hpTone : ''}`);
    w.text(hsHpTip, vm.hpBreakdown);
    const rows = heroStatsRowsHtml(vm), status = heroStatusHtml(vm);
    if (rows !== hsRowsHtml) { hsRowsHtml = rows; hsRows.innerHTML = rows; }
    if (status !== hsStatusHtml) { hsStatusHtml = status; hsStatus.innerHTML = status; }
  }
  const chips = game.points.map(pt => { const el = $('chip-' + pt.id); return { el, bar: el.querySelector('i') as HTMLElement }; });
  const buffEls = new Map<string, { el: HTMLElement; t: HTMLElement }>();
  for (const team of ['a', 'b'] as const) for (const kind of BUFF_KINDS) buffEls.set(`${team}-${kind}`, { el: $(`buff-${team}-${kind}`), t: $(`buffT-${team}-${kind}`) });

  // Stage size for projecting plates: tracked, not read per frame — a getBoundingClientRect() after this
  // frame's DOM writes forces a synchronous layout.
  let stageW = 0, stageH = 0;
  const measureStage = () => { const r = stageEl.getBoundingClientRect(); stageW = r.width; stageH = r.height; };
  measureStage();
  const stageObserver = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measureStage);
  stageObserver?.observe(stageEl);

  const plates = game.heroes.map(h => {
    const el = document.createElement('div');
    el.className = `plate ${h.team}${h.player ? ' me' : ''}`;
    el.innerHTML = `<span class="relic"></span><span class="nm">${heroName(h.kind)}</span><span class="bar"><i class="trail"></i><i class="fill"></i></span><span class="tags"></span>`;
    $('plates').appendChild(el);
    return { el, bar: el.querySelector('.bar i.fill') as HTMLElement, trail: el.querySelector('.bar i.trail') as HTMLElement, tags: el.querySelector('.tags') as HTMLElement, relic: el.querySelector('.relic') as HTMLElement, hpTrail: initHpTrail() };
  });

  // Neutral monsters: one plate per den, shown only while that monster is in view.monsters.
  const monsterPlates = L.bosses.map(b => {
    const el = document.createElement('div');
    el.className = 'plate neutral';
    el.hidden = true;
    el.innerHTML = `<span class="nm">${monsterName(b.kind)}</span><span class="bar"><i class="trail"></i><i class="fill"></i></span>`;
    $('plates').appendChild(el);
    return { el, bar: el.querySelector('.bar i.fill') as HTMLElement, trail: el.querySelector('.bar i.trail') as HTMLElement, hpTrail: initHpTrail() };
  });

  // Monsters present on the PREVIOUS update only — never older, or a fogged fight would leak numbers.
  let prevSeen: SeenMonsters = new Map();
  // Summons seen last frame: a killing blow's number floats where the summon stood (it has left view.summons).
  let prevSummons = new Map<number, { x: number; z: number; y: number }>();
  const buffText = new Map<string, string | null>();  // last written state per buff/row/strip, to skip DOM writes

  const p = player, sk: Record<string, any> = (SKILLS as any)[p.kind] || {};
  const skillsEl = $('skills');
  skillsEl.innerHTML = ['Q', 'W', 'E', 'R'].map(k => {
    const s = sk[k], wide = k === 'E' || k === 'R';
    // E/R/T carry a fixed-size icon box: its image is only set (and fetched) once something is bought into it
    return `<div class="slot${s ? '' : ' empty'}${wide ? ' wide' : ''}" id="slot-${k}">${wide ? '<div class="slot-ico" aria-hidden="true"></div>' : ''}<b>${k}</b><span>${s ? skillName(p.kind, k as 'Q' | 'W') : t('slot.empty')}</span><i></i></div>`;
  }).join('') + `<div class="slot wide relic empty" id="slot-T"><div class="slot-ico" aria-hidden="true"></div><b>T</b><span>${t('slot.noRelic')}</span><i></i></div>`;
  const slotOf = (k: string) => { const el = skillsEl.querySelector('#slot-' + k) as HTMLElement; return { el, name: el.querySelector('span') as HTMLElement, fill: el.querySelector('i') as HTMLElement, ico: el.querySelector('.slot-ico') as HTMLElement | null }; };
  /** The slot's icon, or none: a class for the layout plus the image itself, both skipped when unchanged. */
  const setIcon = (slot: ReturnType<typeof slotOf>, id: ArtifactLine | RelicLine | null) => {
    w.toggle(slot.el, 'has-ico', !!id);
    if (slot.ico) w.style(slot.ico, 'backgroundImage', id ? `url(${iconSrc(id)})` : 'none');
  };
  const slots = { Q: slotOf('Q'), W: slotOf('W'), E: slotOf('E'), R: slotOf('R'), T: slotOf('T') };

  // Hover tooltip over the skill bar: one element above the hovered slot, markup re-assigned only when it
  // changes (a cooldown second ticking, an artefact bought into the hovered slot).
  const tip = document.createElement('div');
  tip.className = 'tip';
  tip.hidden = true;
  skillsEl.appendChild(tip);
  let tipKey: CastKey | null = null, tipShown = '', silenced = '';
  function renderTip() {
    if (!tipKey) return;
    const held = heldOf(p.artifacts);
    const html = (tipKey === 'T' ? relicTipHtml(game.relics[p.team], game.you.relicCd ?? 0)
      : tipKey === 'E' || tipKey === 'R'
      ? artifactTipHtml(tipKey, held[tipKey], cdSecondsLeft(game.artifactCdFor(p, tipKey), held), vialCharge(game.artifactCdFor(p, tipKey)))
      : skillTipHtml(p.kind, tipKey, cdSecondsLeft(p.cd[tipKey], held))) + (silenced ? `<div class="tip-block">${silenced}</div>` : '');
    if (html !== tipShown) { tipShown = html; tip.innerHTML = html; }
  }
  for (const k of ['Q', 'W', 'E', 'R', 'T'] as const) {
    const el = slots[k].el;
    el.addEventListener('pointerenter', () => {
      tipKey = k;
      tip.style.left = `${el.offsetLeft + el.offsetWidth / 2}px`;
      renderTip();
      tip.hidden = false;
    });
    el.addEventListener('pointerleave', () => { tipKey = null; tip.hidden = true; });
  }

  /* The shop never opens itself (a player who only walked home to heal must not have the map covered
     against their will): it opens on a click on the base's own stall mesh (matchView.ts wires input.ts's
     onOpenShop to openShop() below) or the B key, and closes on Esc, the × or leaving the base. `shopOpen`
     is the one source of truth hud.ts's own update() reads each frame. */
  let shopOpen = false;
  function openShop() { if (game.you.inBase) shopOpen = true; }
  function closeShop() { shopOpen = false; }
  /** A press outside the open shop only closes it. True when it did: the caller (the world canvas via
      input.ts, the minimap) must then drop the press rather than turn it into an order. */
  function pressOutsideShop(target: EventTarget | null): boolean {
    if (!shopOpen || $('shop').contains(target as Node | null)) return false;
    closeShop();
    return true;
  }
  // Presses on the rest of the HUD (skill bar, score) close it too. The minimap's own handler ran first and
  // already did; the world canvas is not in `root` — input.ts asks consumeWorldPress() for it, and a stall
  // click that has just opened the shop must not reach this and shut it again.
  const onHudPress = (e: PointerEvent) => { pressOutsideShop(e.target); };
  root.addEventListener('pointerdown', onHudPress);

  // Flat, one-level artefacts (docs/game-design.md's 2026-09-23 rewrite) have no tier text to hide behind
  // a hover-revealed detail dock any more — a card already shows everything at a glance, so `shopBody`'s
  // whole innerHTML is rebuilt from the model rather than patched field by field. It is only ASSIGNED when
  // the markup actually changed (see update()), and clicks are delegated on the stable `#shop` container
  // rather than any one card surviving a rebuild.
  let lastShopItems: ShopItemVM[] = [];
  let lastRelicItems: RelicItemVM[] = [];
  let lastShopHtml = '';
  const buyHold = createBuyHold();
  function performArtifactAction(line: ArtifactLine) {
    const vm = lastShopItems.find(i => i.line === line);
    if (!vm || vm.action.disabledReason) return;
    const held = heldOf(p.artifacts);
    if (vm.action.kind === 'sell') {
      const slot: ArtifactSlotKey | null = held.E === line ? 'E' : held.R === line ? 'R' : null;
      if (slot) { buyHold.sold(slot); game.apply(p.id, { type: 'sellArtifact', slot }); }
    } else {
      const slot = buyHold.pick(held, game.t);
      if (!slot) return;
      buyHold.bought(slot, game.t);
      game.apply(p.id, { type: 'buyArtifact', slot, line });
    }
  }
  function performRelicBuy(line: RelicLine) {
    const vm = lastRelicItems.find(i => i.line === line);
    if (vm && !vm.disabledReason) game.apply(p.id, { type: 'buyRelic', line });
  }
  const shopTargetOf = (e: Event) => {
    const el = (e.target as HTMLElement).closest('[data-shop-buy], [data-shop-sell]') as HTMLButtonElement | null;
    return !el || el.disabled ? null : (el.dataset.shopBuy ?? el.dataset.shopSell) as ArtifactLine;
  };
  const relicTargetOf = (e: Event) => {
    const el = (e.target as HTMLElement).closest('[data-relic-buy]') as HTMLButtonElement | null;
    return !el || el.disabled ? null : el.dataset.relicBuy as RelicLine;
  };
  // `pointerdown`, not `click`: a card only survives between renders (see the innerHTML guard in update()),
  // and a `click` event needs press AND release on the SAME element. A real player holds a mouse button for
  // ~100ms; if anything on the card changes in that window (a lock counting down, ink ticking past the
  // price) the button is replaced mid-press and the click never fires — the reported "the buy buttons
  // don't press even though I have the ink". A press is also simply the right moment for a game HUD.
  $('shop').addEventListener('pointerdown', e => {
    const line = shopTargetOf(e), relic = relicTargetOf(e);
    if (line) { e.preventDefault(); performArtifactAction(line); }
    else if (relic) { e.preventDefault(); performRelicBuy(relic); }
  });
  // Keyboard activation only (`detail === 0` is a click no pointer produced): a mouse click would already
  // have been handled by the pointerdown above, and acting on both would buy twice.
  $('shop').addEventListener('click', e => {
    if ((e as MouseEvent).detail !== 0) return;
    const line = shopTargetOf(e), relic = relicTargetOf(e);
    if (line) performArtifactAction(line);
    else if (relic) performRelicBuy(relic);
  });

  $('shopClose').addEventListener('click', closeShop);
  // A `window` listener needs its own explicit teardown (destroy() below) — this file had no other one
  // before this task, but matchView.ts's own stop() comment is explicit about why an un-removed one is a
  // real bug (fires twice on a second match in the same page session), not a style nit.
  const onShopKey = (e: KeyboardEvent) => {
    if (e.code === 'Escape') { if (shopOpen) closeShop(); return; }
    if (e.code === 'KeyB') { if (shopOpen) closeShop(); else openShop(); }
  };
  window.addEventListener('keydown', onShopKey);

  /* ---- minimap: static layer drawn once, fog and units every frame; RMB sends the hero there ---- */
  const mini = $('minimap') as HTMLCanvasElement, mctx = mini.getContext('2d')!, MW = mini.width, MH = mini.height;
  const mx = (x: number) => (x + L.bounds.x) / (2 * L.bounds.x) * MW, my = (z: number) => (z + L.bounds.z) / (2 * L.bounds.z) * MH;
  const base = document.createElement('canvas');
  base.width = MW; base.height = MH;
  {
    const c = base.getContext('2d')!;
    c.fillStyle = '#e9dcbc'; c.fillRect(0, 0, MW, MH);
    c.fillStyle = 'rgba(70, 110, 60, .55)';
    for (let y = 0; y < MH; y += 2) for (let x = 0; x < MW; x += 2) {
      const wx = x / MW * 2 * L.bounds.x - L.bounds.x, wz = y / MH * 2 * L.bounds.z - L.bounds.z;
      if (L.brush.some(b => inEllipse(wx, wz, b))) c.fillRect(x, y, 2, 2);
    }
    c.lineCap = 'round';
    // by kind: a tract wide and dark, a dirt road as before, a trail thin and dashed
    for (const r of L.roadPaths) {
      const kind = r.kind ?? 'dirt';
      c.strokeStyle = kind === 'tract' ? '#b39562' : '#c9ae78';
      c.lineWidth = kind === 'tract' ? 4.5 : kind === 'dirt' ? 3 : 1.6;
      c.beginPath();
      for (let i = 0; i < r.points.length - 1; i++) {
        const [ax, az] = r.points[i]!, [bx, bz] = r.points[i + 1]!, x0 = mx(ax), y0 = my(az), x1 = mx(bx), y1 = my(bz);
        if (kind !== 'trail') { c.moveTo(x0, y0); c.lineTo(x1, y1); continue; }
        // dashes by hand, 3 px on and 3 off
        const len = Math.hypot(x1 - x0, y1 - y0);
        for (let d = 0; d < len; d += 6) {
          const e = Math.min(len, d + 3);
          c.moveTo(x0 + (x1 - x0) * d / len, y0 + (y1 - y0) * d / len); c.lineTo(x0 + (x1 - x0) * e / len, y0 + (y1 - y0) * e / len);
        }
      }
      c.stroke();
    }
    c.strokeStyle = '#6b8793'; c.lineWidth = 3;
    for (const st of L.streams) { c.beginPath(); st.forEach(([x, z], i) => (i ? c.lineTo(mx(x), my(z)) : c.moveTo(mx(x), my(z)))); c.stroke(); }
    c.fillStyle = '#8a6a48';
    for (const [x, z] of L.bridges) c.fillRect(mx(x) - 2.5, my(z) - 2.5, 5, 5);
    c.fillStyle = '#2f5a35';
    for (const [x, z] of L.trees) c.fillRect(mx(x) - 1, my(z) - 1, 2, 2);
    for (const [team, [x, z]] of Object.entries(L.bases) as [Team, [number, number]][]) {
      c.fillStyle = team === 'a' ? '#c9533f' : '#3f64a0';
      c.fillRect(mx(x) - 5, my(z) - 5, 10, 10);
    }
  }
  mini.addEventListener('contextmenu', e => e.preventDefault());
  mini.addEventListener('pointerdown', e => {
    if (pressOutsideShop(e.target) || e.button !== 2) return;
    const r = mini.getBoundingClientRect();
    const x = (e.clientX - r.left) / r.width * 2 * L.bounds.x - L.bounds.x;
    const z = (e.clientY - r.top) / r.height * 2 * L.bounds.z - L.bounds.z;
    game.apply(p.id, { type: 'move', x, z });
  });
  const fog = document.createElement('canvas');
  fog.width = MW; fog.height = MH;
  function drawMinimap(monsters: readonly ViewMonster[]) {
    mctx.drawImage(base, 0, 0);
    for (const pt of game.points) {
      mctx.beginPath(); mctx.arc(mx(pt.pos.x), my(pt.pos.z), 6, 0, 7);
      mctx.fillStyle = pt.owner === 'a' ? '#c9533f' : pt.owner === 'b' ? '#3f64a0' : '#f2d46b'; mctx.fill();
      mctx.lineWidth = 1.5; mctx.strokeStyle = '#2c1e12'; mctx.stroke();
      mctx.fillStyle = '#2c1e12'; mctx.font = 'bold 9px serif'; mctx.textAlign = 'center'; mctx.fillText(pt.id, mx(pt.pos.x), my(pt.pos.z) + 3);
    }
    const f = fog.getContext('2d')!;
    f.globalCompositeOperation = 'source-over';
    f.clearRect(0, 0, MW, MH);
    f.fillStyle = 'rgba(44, 30, 18, .38)'; f.fillRect(0, 0, MW, MH);
    f.globalCompositeOperation = 'destination-out';
    for (const poly of vision.polys) {
      f.beginPath();
      poly.forEach(([x, z]: [number, number], i: number) => (i ? f.lineTo(mx(x), my(z)) : f.moveTo(mx(x), my(z))));
      f.closePath(); f.fill();
    }
    mctx.drawImage(fog, 0, 0);
    // Monsters: a sepia diamond, larger for the Бегемот, only while seen and alive.
    for (const m of monsters) {
      const x = mx(m.pos.x), y = my(m.pos.z), k = m.kind === 'behemoth' ? 6 : 5;
      mctx.beginPath(); mctx.moveTo(x, y - k); mctx.lineTo(x + k, y); mctx.lineTo(x, y + k); mctx.lineTo(x - k, y); mctx.closePath();
      mctx.fillStyle = '#6b4a1c'; mctx.fill();
      mctx.lineWidth = 1.5; mctx.strokeStyle = '#efe3c6'; mctx.stroke();
    }
    const confused = viewerConfused(game);
    for (const h of game.heroes) {
      if (!h.alive || !game.visibleTo(p.team, h)) continue;
      mctx.beginPath(); mctx.arc(mx(h.pos.x), my(h.pos.z), h.player ? 4.5 : 3.5, 0, 7);
      mctx.fillStyle = shownTeam(h, p, confused) === 'a' ? '#c9533f' : '#3f64a0'; mctx.fill();
      mctx.lineWidth = h.player ? 2 : 1; mctx.strokeStyle = h.player ? '#fff4e4' : '#2c1e12'; mctx.stroke();
    }
  }

  /* Damage from skills (sepia) and healing (green) float up from the hero or monster; basic hits stay silent,
     except a Туманный амулет miss («промах»). */
  // `unit` null: anchored at a fixed point (a killing blow's number, whose monster is already gone).
  const floats: { el: HTMLElement; unit: Unit | null; pos: { x: number; z: number }; y: number; t0: number; dx: number }[] = [];
  const unitSeen = (u: Unit | null, monsters: readonly ViewMonster[]) =>
    u === null || (isMonsterUnit(u) ? monsters.includes(u) : isSummonUnit(u) ? game.summons.includes(u) : game.visibleTo(p.team, u));
  function spawnNumber(unit: Unit | null, pos: { x: number; z: number }, y: number, value: number, heal?: boolean, miss?: boolean) {
    const el = document.createElement('div');
    el.className = 'num' + (miss ? ' miss' : heal ? ' heal' : value >= 25 ? ' big' : '');
    el.textContent = miss ? t('hud.miss') : (heal ? '+' : '') + value;
    E.nums.appendChild(el);
    floats.push({ el, unit, pos, y, t0: performance.now(), dx: (Math.random() - 0.5) * 30 });
  }
  function drawNumbers(r: { width: number; height: number }, monsters: readonly ViewMonster[]) {
    for (let i = floats.length - 1; i >= 0; i--) {
      const f = floats[i]!, k = (performance.now() - f.t0) / 900;
      // A monster number outlives its monster (the killing blow's neighbours): finish it where it stood.
      if (f.unit && ((isMonsterUnit(f.unit) && !monsters.includes(f.unit)) || (isSummonUnit(f.unit) && !game.summons.includes(f.unit)))) { f.pos = { x: f.pos.x, z: f.pos.z }; f.unit = null; }
      if (k >= 1 || !unitSeen(f.unit, monsters)) { f.el.remove(); floats.splice(i, 1); continue; }
      tmp.set(f.pos.x, f.y, f.pos.z).project(camera);
      f.el.style.transform = `translate(${(tmp.x * 0.5 + 0.5) * r.width + f.dx}px, ${(-tmp.y * 0.5 + 0.5) * r.height - k * 40}px) translate(-50%, -100%)`;
      f.el.style.opacity = String(1 - k * k);
    }
  }

  let toastUntil = 0;
  let lastInkFloor = -1;
  const allyLoadouts = new Map<number, HeldArtifacts>();
  let results: ReturnType<typeof mountResults> | null = null;
  let stallHintShown = false, stallHintUntil = 0;
  function announce(text: string) {
    const el = document.createElement('div');
    el.textContent = text;
    E.relicFeed.appendChild(el);
    setTimeout(() => el.remove(), 5000);
  }
  function toast(text: string) {
    w.text(E.toast, text);
    w.hidden(E.toast, false);
    toastUntil = performance.now() + 1600;
  }

  const tmp = new THREE.Vector3();
  const place = (el: HTMLElement, x: number, y: number, z: number) => {
    tmp.set(x, y, z).project(camera);
    w.style(el, 'transform', `translate(${(tmp.x * 0.5 + 0.5) * stageW}px, ${(-tmp.y * 0.5 + 0.5) * stageH}px) translate(-50%, -100%)`);
  };
  const gate = createMinimapGate();
  const stage = { width: 0, height: 0 };
  function update(input: Input) {
    const own = game.statusOf(p);
    silenced = p.alive ? silenceReason(own.forced, own.forcedFor ?? 0) : '';
    const confused = viewerConfused(game);
    renderTip();
    w.text(E.ptsA, String(game.score.a));
    w.text(E.ptsB, String(game.score.b));
    const left = Math.max(0, RULES.matchTime - game.clock);
    w.text(E.clock, game.overtime ? t('hud.overtime')
      : `${String(Math.floor(left / 60)).padStart(2, '0')}:${String(Math.floor(left % 60)).padStart(2, '0')}`);
    game.points.forEach((pt, i) => {
      const c = chips[i]!;
      w.className(c.el, `chip ${pt.owner || ''}${pt.contested ? ' fight' : ''}`);
      w.style(c.bar, 'width', `${Math.abs(pt.progress) * 100}%`);
      w.style(c.bar, 'background', pt.progress >= 0 ? 'var(--red)' : 'var(--blue)');
    });

    const nowMs = performance.now();
    updateHeroStats(nowMs);
    const relics = game.relics;
    w.hidden(hsNote, !(p.alive && own.sleepless));
    w.hidden(veil, !confused);
    game.heroes.forEach((h, i) => {
      const pl = plates[i]!, shown = h.alive && game.visibleTo(p.team, h);
      w.hidden(pl.el, !shown);
      updateHpBar(w, pl, shown, h.hp, game.maxHpOf(h), nowMs);
      if (!shown) return;
      place(pl.el, h.pos.x, 2.9, h.pos.z);
      const st = game.statusOf(h), team = shownTeam(h, p, confused);
      w.toggle(pl.el, 'a', team === 'a');
      w.toggle(pl.el, 'b', team === 'b');
      w.toggle(pl.el, 'stun', game.t < h.stunUntil);
      w.toggle(pl.el, 'charm', st.forced === 'charm');
      w.toggle(pl.el, 'fear', st.forced === 'fear');
      w.toggle(pl.el, 'brace', h.state === 'brace');
      w.text(pl.tags, plateTags(st));
      w.text(pl.relic, relicPlateText(relics[h.team]));
    });
    // Read once per frame: the local view's getter filters by sight and allocates on every access.
    const monsters = game.monsters;
    monsterPlates.forEach((pl, id) => {
      const m = monsters.find(v => v.id === id);
      w.hidden(pl.el, !m);
      updateHpBar(w, pl, !!m, m?.hp ?? 0, m?.maxHp ?? 1, nowMs);
      if (!m) return;
      place(pl.el, m.pos.x, MONSTER_LOOK[m.kind].plateY, m.pos.z);
    });

    const buffs = game.teamBuffs;
    let anyBuff = false;
    for (const team of ['a', 'b'] as const) {
      let rowBuff = false;
      for (const kind of BUFF_KINDS) {
        const left = buffs[team][kind], text = left === null ? null : mmss(Math.ceil(left)), key = `${team}-${kind}`;
        rowBuff ||= text !== null;
        if (buffText.get(key) === text) continue;
        buffText.set(key, text);
        const b = buffEls.get(key)!;
        b.el.hidden = text === null;
        if (text !== null) b.t.textContent = text;
      }
      anyBuff ||= rowBuff;
      if (buffText.get(`row-${team}`) !== String(rowBuff)) { buffText.set(`row-${team}`, String(rowBuff)); $(`buffRow-${team}`).hidden = !rowBuff; }
    }
    if (buffText.get('strip') !== String(anyBuff)) { buffText.set('strip', String(anyBuff)); E.buffs.hidden = !anyBuff; }

    const held = heldOf(p.artifacts);
    for (const k of ['Q', 'W'] as const) {
      const s = sk[k];
      if (!s) continue;
      const slot = slots[k], cd = p.cd[k];
      const charging = k === 'Q' && p.state === 'charging';
      w.toggle(slot.el, 'cooling', cd > 0);
      w.toggle(slot.el, 'silenced', !!silenced);
      w.toggle(slot.el, 'aiming', input.targeting === k || charging);
      w.style(slot.fill, 'height', charging ? `${game.chargeLevel(p) * 100}%` : `${(cd / s.cd) * 100}%`);
      w.toggle(slot.el, 'charge', charging);
      w.data(slot.el, 'cd', cd > 0 ? String(Math.ceil(cdSecondsLeft(cd, held))) : '');
    }
    for (const k of ARTIFACT_SLOTS) {
      const slot = slots[k], line = held[k], def = line ? ARTIFACTS[line] : null;
      w.toggle(slot.el, 'empty', !def);
      setIcon(slot, line);
      w.toggle(slot.el, 'silenced', !!silenced && !!def && def.kind === 'active');
      if (line && def && def.kind === 'active') {
        const v = activeSlotView(line, game.artifactCdFor(p, k), held);
        w.text(slot.name, artifactName(line));
        w.toggle(slot.el, 'passive', false);
        w.toggle(slot.el, 'cooling', v.cooling);
        w.toggle(slot.el, 'vial', v.vial);
        w.toggle(slot.el, 'aiming', input.targeting === k);
        w.style(slot.fill, 'height', `${v.fill * 100}%`);
        w.data(slot.el, 'cd', v.cdText);
      } else if (def) {
        // Passive: no button does anything here, no cooldown to show — it's already working.
        w.text(slot.name, artifactName(line!));
        w.toggle(slot.el, 'passive', true);
        w.toggle(slot.el, 'cooling', false); w.toggle(slot.el, 'vial', false); w.toggle(slot.el, 'aiming', false);
        w.style(slot.fill, 'height', '0%');
        w.data(slot.el, 'cd', '');
      } else {
        w.text(slot.name, t('slot.inShop'));
        w.toggle(slot.el, 'cooling', false); w.toggle(slot.el, 'vial', false); w.toggle(slot.el, 'aiming', false); w.toggle(slot.el, 'passive', false);
        w.style(slot.fill, 'height', '0%');
        w.data(slot.el, 'cd', '');
      }
    }

    {
      const slot = slots.T, v = relicSlotView(relics[p.team], game.you.relicCd ?? 0);
      w.text(slot.name, v.name);
      setIcon(slot, relics[p.team]);
      w.toggle(slot.el, 'empty', v.empty);
      w.toggle(slot.el, 'passive', v.passive);
      w.toggle(slot.el, 'cooling', v.cooling);
      w.toggle(slot.el, 'silenced', !!silenced && !v.empty && !v.passive);
      w.style(slot.fill, 'height', `${v.fill * 100}%`);
      w.data(slot.el, 'cd', v.cdText);
    }

    // A finished match's reset clears every slot: no toasts across it.
    if (game.winner) allyLoadouts.clear();
    else {
      const allies = game.heroes.filter(h => h !== p && h.team === p.team && game.visibleTo(p.team, h));
      const news = allyShopToasts(allyLoadouts, allies);
      if (news.length) toast(news.join(' · '));
    }

    const you = game.you;
    {
      const floor = Math.floor(you.ink);
      w.text(E.inkAmt, String(floor));
      if (floor !== lastInkFloor && INK_MILESTONES.includes(floor)) {
        E.ink.classList.remove('pulse'); void E.ink.offsetWidth; E.ink.classList.add('pulse');
      }
      lastInkFloor = floor;
    }
    if (!you.inBase) shopOpen = false;  // left the base: the shop can't apply, so it can't stay open either
    const shopVisible = you.inBase && shopOpen;
    w.hidden(E.shop, !shopVisible);
    // Below ~1124px the shop reaches the bottom-right corner, and the stats block would sit over (and
    // swallow clicks on) its right-most card.
    w.hidden(E.hstats, shopVisible);
    if (shopVisible) {
      const items = buildShopItems(held, you), relicItems = buildRelicItems(relics, p.team, you);
      lastShopItems = items;
      lastRelicItems = relicItems;
      // Only when the markup actually changed: re-assigning innerHTML 60 times a second destroys and
      // rebuilds every card each frame, which throws away focus, hover state and — the bug this guard
      // exists for — any press in progress on a button.
      const html = shopBodyHtml(items, relicItems);
      if (html !== lastShopHtml) { lastShopHtml = html; E.shopBody.innerHTML = html; }
    }

    // A one-time nudge, not a nag: the first frame the player is home with something to spend, for as long
    // as they haven't already opened the shop some other way. Positioned over the stall itself, the same
    // world point mapView.ts built it at, so main.ts/matchView.ts need not pass its screen position down.
    if (you.inBase && you.ink > 0 && !shopOpen && !stallHintShown) { stallHintShown = true; stallHintUntil = performance.now() + 4500; }
    const hintOn = you.inBase && !shopOpen && performance.now() < stallHintUntil;
    w.hidden(E.stallHint, !hintOn);
    if (hintOn) {
      const [sx, sz] = baseParts(p.team).shop;
      place(E.stallHint, sx, 2.6, sz);
    }

    for (const e of game.events) {
      if (e.type === 'relic') { announce(relicEventText(e)); continue; }
      if (e.type === 'monsterNum') {
        const a = monsterNumAnchor(e.monster, monsters, prevSeen);
        if (a) spawnNumber(a.unit, a.pos, MONSTER_LOOK[a.kind].plateY + 0.3, e.value);
        continue;
      }
      if (e.type === 'summonNum') {  // a summon out of view (or already gone) shows no number
        const s = game.summons.find(v => v.id === e.summon), last = prevSummons.get(e.summon);
        if (s) spawnNumber(s, s.pos, SUMMON_LOOK[s.kind].barY + 0.3, e.value);
        else if (last) spawnNumber(null, { x: last.x, z: last.z }, last.y, e.value);
        continue;
      }
      if (e.type !== 'num') continue;
      const hero = game.heroes[e.hero];
      if (hero && game.visibleTo(p.team, hero)) spawnNumber(hero, hero.pos, 3.2, e.value, e.heal, e.miss);
    }
    prevSeen = seenMonsters(monsters);
    prevSummons = new Map(game.summons.map(v => [v.id, { x: v.pos.x, z: v.pos.z, y: SUMMON_LOOK[v.kind].barY + 0.3 }]));
    stage.width = stageW; stage.height = stageH;
    drawNumbers(stage, monsters);

    if (!p.alive) { w.hidden(E.status, false); w.text(E.status, t('status.respawn', { s: Math.ceil(p.respawnAt - game.t) })); }
    else if (own.forced) { w.hidden(E.status, false); w.text(E.status, t(own.forced === 'charm' ? 'status.line.charm' : 'status.line.fear')); }
    else if (confused) { w.hidden(E.status, false); w.text(E.status, t('status.line.delusion')); }
    else if (own.invisible) { w.hidden(E.status, false); w.text(E.status, t('status.line.meld')); }
    else if (own.silhouette) { w.hidden(E.status, false); w.text(E.status, t('status.line.silhouette')); }
    else if (inBrush(p.pos.x, p.pos.z)) { w.hidden(E.status, false); w.text(E.status, t('status.line.grass')); }
    else w.hidden(E.status, true);

    if (game.winner && !results) {
      const stats = game.matchStats;
      if (stats) {
        closeShop();
        const second = secondary?.() ?? null;
        results = mountResults(root, { winner: game.winner, score: { ...game.score }, stats, viewerTeam: p.team, viewerId: p.player ? p.id : null },
          { primaryLabel: pickAgainLabel, onPrimary: onPickAgain, secondaryLabel: second?.label, onSecondary: second ? () => second.run() : undefined });
      }
    } else if (!game.winner && results) { results.destroy(); results = null; }
    w.hidden(E.banner, !game.winner || !!results);
    if (game.winner && !results) {
      w.text(E.bannerTitle, t('banner.won', { team: teamFullName(game.winner) }));
      w.text(E.bannerSub, bannerSubText(game));
    }
    if (performance.now() > toastUntil) w.hidden(E.toast, true);

    gate.begin();
    for (const pt of game.points) gate.put(pt.owner === 'a' ? 1 : pt.owner === 'b' ? 2 : 0);
    gate.put(monsters.length);
    for (const m of monsters) { gate.put(m.kind === 'behemoth' ? 1 : 2); gate.put(m.pos.x); gate.put(m.pos.z); }
    for (const h of game.heroes) {
      const on = h.alive && game.visibleTo(p.team, h);
      gate.put(on ? 1 : 0);
      if (on) { gate.put(h.pos.x); gate.put(h.pos.z); }
    }
    gate.put(confused ? 1 : 0);
    gate.polys(vision.polys);
    if (gate.end()) drawMinimap(monsters);
  }

  function destroy() { window.removeEventListener('keydown', onShopKey); root.removeEventListener('pointerdown', onHudPress); stageObserver?.disconnect(); results?.destroy(); results = null; }

  return { update, toast, destroy, openShop, consumeWorldPress: () => pressOutsideShop(null) };
}
